import { ButtonType, DialogFooterButtonsWidget, DialogPositioningBehavior, DialogType, DialogWidget, TabContent, TabsWidget } from '@tableau/widgets';
import React from 'react';

import { ReactModalService } from '~commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { L10n } from '~commonInfra/L10n/L10n';
import { CommonStyles } from '~commonInfra/styles/CommonStyles';
import { Messages } from '~src/codegen/Localize';
import { IExtractTaskSchedule } from '~src/extractTask/actions/CreateExtractTaskAction';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { IScheduleInfo } from '~src/schedule/types/IScheduleInfo';
import { cssClass, Style } from '~src/style';
import { IDatasourceTab, IWorkbookTab } from '~src/uploadContent/actions/PublishWorkbookAction';

import { TabbedScheduleDialogTabBody, TabbedScheduleDialogTabBodyProps } from './TabbedScheduleDialogTabBody';

export const tabLabelCSS = cssClass('schedule-tab', 'label', {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    maxWidth: 'fit-content',
    fontSize: CommonStyles.Sizes.NormalFontSize
});

export interface TabbedScheduleDialogWithSSSProps {
    workbookTab: IWorkbookTab;
    datasourceTabs: IDatasourceTab[];
    scheduleOptions: IScheduleInfo[];
    close: (result?: IResult) => void;
    setSchedules: (workbookTab: IWorkbookTab, datasourceTabs: IDatasourceTab[]) => void;
}

export function openTabbedScheduleDialogWithSSS(props: Omit<TabbedScheduleDialogWithSSSProps, 'close'>) {
    return ReactModalService.openCustom<IResult>(closeAndDismiss => <TabbedScheduleDialogWithSSS {...{ ...props, close: closeAndDismiss.close }} />);
}

export function TabbedScheduleDialogWithSSS(props: TabbedScheduleDialogWithSSSProps) {
    const [workbookTab, setWorkbookTab] = React.useState(props.workbookTab);
    const [datasourceTabs, setDatasourceTabs] = React.useState({ tabs: props.datasourceTabs });

    // Create tab body content for workbook tab, if there is one
    let workbookTabBody: JSX.Element;
    if (workbookTab) {
        const workbookTabProps: TabbedScheduleDialogTabBodyProps = {
            prompt: Messages.schedulesLabelPromptWorkbook({ WORKBOOK_NAME: L10n.elideIfLong(workbookTab.workbook.name) }),
            allowIncremental: workbookTab.workbook.hasIncrementalExtract,
            scheduleOptions: props.scheduleOptions,
            setExtractSchedule: (schedule: IExtractTaskSchedule) => {
                setWorkbookTab(previousState => ({ ...previousState, extractTask: schedule }));
            }
        };
        workbookTabBody = TabbedScheduleDialogTabBody(workbookTabProps);
    }

    // Create tab body content for each datasource, if any
    let datasourceTabBodies: JSX.Element[] = [];
    datasourceTabs.tabs.forEach((datasourceTab: IDatasourceTab, index: number) => {
        const datasourceTabBodyProps: TabbedScheduleDialogTabBodyProps = {
            prompt: Messages.schedulesLabelPromptDatasource({ DATASOURCE_NAME: L10n.elideIfLong(datasourceTab.datasource.name) }),
            allowIncremental: datasourceTab.datasource.hasIncrementalExtract,
            scheduleOptions: props.scheduleOptions,
            setExtractSchedule: (schedule: IExtractTaskSchedule) => {
                setDatasourceTabs(previousState => ({
                    tabs: previousState.tabs.map(
                        (tab, tabIndex) => tabIndex === index ? { ...tab, extractTask: schedule } : tab)
                }));
            }
        };
        let datasourceTabBody = TabbedScheduleDialogTabBody(datasourceTabBodyProps);
        datasourceTabBodies.push(datasourceTabBody);
    });

    // Add all tab bodies to TabWidget
    let tabs = [];
    workbookTab && tabs.push({
        tabContent: {
            title: workbookTab.workbook.name,
            icon: getTabLabel(workbookTab.workbook.name, workbookTab.iconClassFn())
        } as TabContent,
        panelContent: workbookTabBody
    });
    datasourceTabBodies.forEach((tabBody, index) => {
        tabs.push({
            tabContent: {
                title: datasourceTabs.tabs[index].datasource.name,
                icon: getTabLabel(datasourceTabs.tabs[index].datasource.name, datasourceTabs.tabs[index].iconClassFn())
            } as TabContent,
            panelContent: tabBody
        });
    });

    const baseHeaderStyle: Style = {
        fontWeight: 'normal',
        fontSize: 20,
        marginTop: '0px',
        marginBottom: '18px'
    };
    const dialogHeaderStyle = cssClass('tabbed-schedule', 'dialog-header', baseHeaderStyle);

    return <DialogWidget
        testId='tabbed-schedule-dialog-sss'
        type={DialogType.NormalWithoutTitleBar}
        title={Messages.publishWorkbookScheduleDialogTitle()}
        onCloseRequest={props.close}
        showCloseButton={true}
        positioningBehavior={DialogPositioningBehavior.Centered}
        isModal={true}
        handleGlassClick={props.close}
        isGlassTransparent={false}
        footerContent={renderFooter(props, workbookTab, datasourceTabs.tabs)}
        dialogWidth={TabDialogWidth.width700px}
        dialogStyle={{ paddingLeft: 12, paddingRight: 12 }}
    >
        <h1 className={dialogHeaderStyle}>{Messages.publishWorkbookScheduleDialogTitle()}</h1>
        {
            <TabsWidget
                ariaLabel={Messages.publishWorkbookScheduleDialogTitle()}
                initialActiveIndex={0}
                tabs={tabs}
                testId='tabbed-schedule-dialog-tabs'
            />
        }
    </DialogWidget>;
}

function renderFooter(props: TabbedScheduleDialogWithSSSProps, workbookTab: IWorkbookTab, datasourceTabs: IDatasourceTab[]) {
    return <DialogFooterButtonsWidget
        buttons={[
            {
                handleClick: () => props.close(),
                buttonType: ButtonType.Outline,
                children: Messages.cancel(),
                testId: 'footer-cancel'
            },
            {
                handleClick: () => {
                    props.close();
                    props.setSchedules(workbookTab, datasourceTabs);
                },
                buttonType: ButtonType.Primary,
                children: Messages.publishWorkbookScheduleDialogButton_schedule(),
                testId: 'footer-confirm'
            }
        ]}
        testId='tabbed-dialog-sss-footer'
    />;
}

function getTabLabel(tabName: string, iconClass: string) {
    return <>
        <div className={tabLabelCSS}>
            <div className={iconClass} style={{ minWidth: CommonStyles.Sizes.SmallIconSize }} />
            <span>{tabName}</span>
        </div>
    </>;
}
